import React from "react";
import { motion } from "motion/react";
import {
  ArrowRight,
  Users,
  Award,
  Zap,
  Code,
  Smartphone,
  Brain,
  BarChart3,
  ShoppingCart,
  TrendingUp,
  Headphones,
  MonitorSmartphone,
} from "lucide-react";
import { Button } from "../ui/Button";
import { Card, CardContent } from "../ui/Card";
import { Badge } from "../ui/Badge";
import HeroSection from "./Sections/HeroSection";
import MetricsSection from "./Sections/MetricsSection";
import ServiceSection from "./Sections/ServiceSection";
import ClientsReview from "./Sections/ClientsReview";
import MetaTagComponent from "../common/Meta/MetaTagComponent";
import { metaDetailsHome } from "../../Utilites";
import { Link } from "react-router-dom";

const services = [
  {
    icon: BarChart3,
    title: "Digital Marketing",
    description:
      "Modernize legacy systems and workflows with end-to-end transformation services that unlock growth, agility, and innovation.",
    gradient: "linear-gradient(90deg, #ec4899, #f87171)",
    link:'/digital-marketing-service'
  },
  {
    icon: Code,
    title: "Web Development",
    description:
      "We build high-performance, responsive websites and web apps tailored to your brand and business goals — from startups to enterprise platforms.",
    gradient: "linear-gradient(90deg, #3b82f6, #06b6d4)",
    link:'/web-development-service'
  },
  {
    icon: Smartphone,
    title: "Mobile App Development",
    description: "From iOS to Android, our apps are designed for performance, security, and seamless user experience across all devices.",
    gradient: "linear-gradient(90deg, #10b981, #34d399)",
    link:'/mobile-app-development-service'
  },
  {
    icon: Brain,
    title: "AI & Machine Learning",
    description: "Integrate intelligence into your product with our AI and ML solutions — including automation, predictive analytics, and smart algorithms.",
    gradient: "linear-gradient(90deg, #a78bfa, #6366f1)",
    link:'/ai-development-service'
  },
  {
    icon: MonitorSmartphone,
    title: "UI/UX Design",
    description:
      "We design intuitive, beautiful user interfaces that engage users and enhance experiences across web and mobile platforms.",
    gradient: "linear-gradient(135deg, #ec4899, #f43f5e)",
    link:'/ui-ux-development-service'
  },
  {
    icon: ShoppingCart,
    title: "E-Commerce",
    description: "We build secure, scalable ecommerce platforms that drive conversions — from custom storefronts to seamless payment integrations and inventory automation.",
    gradient: "linear-gradient(90deg, #14b8a6, #3b82f6)",
    link:'/ecommerce-development-service'
  },
];

const successMetrics = [
  { number: 500, label: "Projects Delivered", suffix: "+", icon: TrendingUp },
  { number: 200, label: "Satisfied Clients", suffix: "+", icon: Users },
  { number: 24, label: "Support Availability", suffix: "/7", icon: Headphones },
  { number: 99, label: "Success Rate", suffix: "%", icon: Award },
];

const testimonials = [
  {
    name: "Sarah Johnson",
    position: "CEO, TechStart Inc.",
    content:
      "SoftwareCo transformed our vision into reality. Their expertise in AI development helped us revolutionize our product.",
    rating: 5,
    image:
      "https://images.unsplash.com/photo-1494790108755-2616b25d4e35?w=150&h=150&fit=crop&crop=face",
  },
  {
    name: "Michael Chen",
    position: "Founder, E-Commerce Plus",
    content:
      "Outstanding web development services. They delivered a beautiful, fast, and conversion-optimized website.",
    rating: 5,
    image:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
  },
  {
    name: "Emily Rodriguez",
    position: "Marketing Director, GrowthCo",
    content:
      "Their digital marketing expertise doubled our online revenue within 6 months. Highly recommended!",
    rating: 5,
    image:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
  },
];

const clientLogos = [
  "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=120&h=60&fit=crop",
  "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=120&h=60&fit=crop",
  "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=120&h=60&fit=crop",
  "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=120&h=60&fit=crop",
  "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=120&h=60&fit=crop",
  "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=120&h=60&fit=crop",
];

const blogPosts = [
  {
    title: "The Future of AI in Software Development",
    excerpt:
      "Explore how artificial intelligence is revolutionating the way we build software...",
    date: "Dec 15, 2024",
    category: "AI Technology",
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop",
    slug: "future-of-ai-software-development",
  },
  {
    title: "Building Scalable E-Commerce Platforms",
    excerpt:
      "Learn the key principles and technologies for creating robust online stores...",
    date: "Dec 12, 2024",
    category: "E-Commerce",
    image:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=250&fit=crop",
    slug: "building-scalable-ecommerce-platforms",
  },
  {
    title: "Mobile-First Design Strategies",
    excerpt:
      "Discover best practices for creating mobile-optimized user experiences...",
    date: "Dec 10, 2024",
    category: "Design",
    image:
      "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=250&fit=crop",
    slug: "mobile-first-design-strategies",
  },
];

export default function MfHome() {
  return (
    <div className="min-vh-100">
      <MetaTagComponent metaDetails={metaDetailsHome} />
      {/* Hero Section */}
      <HeroSection />

      {/* Success Metrics Section */}
      <MetricsSection successMetrics={successMetrics} />

      {/* Services Preview */}
      <ServiceSection services={services} />

      {/* Testimonials */}
      <ClientsReview testimonials={testimonials} />

      {/* Blog Preview */}
      <section className="py-5 bg-white">
        <div className="container px-3">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-5"
          >
            <Badge className="mb-2 bg-primary text-white border-0">
              Latest Insights
            </Badge>
            <h2 className="display-4 fw-bold mb-3 text-primary">
              From Our Blog
            </h2>
            <p className="lead text-muted mb-4">
              Stay updated with the latest trends, insights, and best practices
              in technology and digital innovation.
            </p>
          </motion.div>

          <div className="row row-cols-1 row-cols-md-3 g-4">
            {blogPosts.map((post, index) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <Card className="h-100 rounded-3 border-0 shadow-sm overflow-hidden">
                  <div className="position-relative overflow-hidden">
                    <Badge className="position-absolute top-0 start-0 m-3 bg-primary text-white border-0">
                      {post.category}
                    </Badge>
                  </div>
                  <CardContent className="p-3">
                    <p className="text-muted small mb-1">{post.date}</p>
                    <h5 className="mb-2">{post.title}</h5>
                    <p className="text-muted mb-3">{post.excerpt}</p>
                    <Button variant="ghost" className="btn btn-link p-0">
                      Read More <ArrowRight className="ms-2" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 bg-primary text-white text-center">
        <div className="container px-3">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="display-6 fw-bold mb-3">
              Ready to Transform Your Business?
            </h2>
            <p className="lead mb-4">
              Let's discuss how our premium software solutions can drive your
              success. Get started with a free consultation today.
            </p>
            <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
              <Link to={"/contact"} className="btn btn-light btn-lg rounded-pill px-2">
                Get Free Consultation <ArrowRight className="ms-2" />
              </Link>
              {/* <Button
                variant="outline"
                className="btn btn-outline-light btn-lg rounded-pill px-5"
              >
                View Portfolio
              </Button> */}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
